import React from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

/**
 * Error boundary specifically for the feedback system
 * Provides graceful error handling and recovery options
 */
class FeedbackErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error('Feedback system error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Report error to monitoring service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: `Feedback Error: ${error.message}`,
        fatal: false
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
              <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>

            {/* Error Message */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Feedback System Error
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Something went wrong with the feedback system. This might be a temporary issue.
              </p>

              {/* Error Details (only in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6 text-left">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    Error Details:
                  </h4>
                  <p className="text-sm text-red-600 dark:text-red-400 font-mono">
                    {this.state.error.message}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={this.handleRetry}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 
                           bg-purple-600 hover:bg-purple-700 text-white rounded-lg
                           transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                >
                  <ArrowPathIcon className="w-4 h-4" />
                  Try Again
                </button>
                
                <button
                  onClick={() => {
                    // Close the feedback system
                    if (this.props.onClose) {
                      this.props.onClose();
                    }
                    this.handleRetry(); // Reset error state
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 
                           text-gray-700 dark:text-gray-300 rounded-lg
                           hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                           focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>

              {/* Retry Count */}
              {this.state.retryCount > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                  Retry attempts: {this.state.retryCount}
                </p>
              )}

              {/* Alternative Contact */}
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  If the problem persists, you can reach us at{' '}
                  <a 
                    href="mailto:support@justburnt.com" 
                    className="text-purple-600 dark:text-purple-400 hover:underline"
                  >
                    support@justburnt.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default FeedbackErrorBoundary;